import React, { FC } from "react";
import { useQuery } from "react-query";
import { Helmet } from "react-helmet";
import makeStyles from "@material-ui/core/styles/makeStyles";
import ScrollTop from "../../../components/ScrollToTop";
import { endOfTitle } from "../../../constants/constants";
import Loading from "../../../components/Loading";
import Error from "../../../components/Error";
import { QUERY_KEYS } from "../../../constants";
import { useSchoolId } from "../../school/hooks/useSchoolId";
import { EducationType } from "../../school/constants/types";
import { useAuth } from "../../auth/hooks/useAuth";
import { ISchoolStudentApplicationsList } from "../../student/types";
import { parseToSchoolStudentCardsProgramFrontEndModel } from "../helpers/parseToSchoolStudentCardsProgramFrontEndModel";
import { useApplicationQueries } from "../hooks/useApplicationQueries";
import { UserRole } from "../../user/enums/UserRole";
import { useUserAccount } from "../../user/userAccount/context";
import { parseToSchoolStudentCardsLanguageCourseFrontEndModel } from "../helpers/parseToSchoolStudentCardsLanguageCourseFrontEndModel";
import { SchoolStudentApplicationCard } from "./card/SchoolStudentApplicationCard";

const useStyles = makeStyles(() => ({
  root: {
    display: "flex",
    justifyContent: "center",
  },
  loading: {
    "& > div": {
      position: "static",
      backgroundColor: "unset",
      padding: "280px 0",
    },
  },
  error: {
    marginTop: 200,
  },
}));

export const SchoolStudentApplications: FC<ISchoolStudentApplicationsList> = ({
  studentId,
}) => {
  const classes = useStyles();

  const schoolId = useSchoolId();
  const {
    user,
    profile: { data: { data: profile = null } = {} },
  } = useAuth();

  //admin case
  const { profile: accountProfile } = useUserAccount();

  const isAdminOrSupport = [
    UserRole.Owner,
    UserRole.Admin,
    UserRole.Support,
  ].includes(user.role);
  const schoolType = isAdminOrSupport
    ? accountProfile?.data.data.school?.typeId
    : profile?.school?.typeId;
  const { schoolStudentApplications } = QUERY_KEYS;

  const { fetchStudentApplications } = useApplicationQueries();
  const { isLoading: isLoading, data: { data: applications = [] } = {} } =
    useQuery<any>([schoolStudentApplications, studentId + schoolId], () =>
      fetchStudentApplications(
        {
          studentId,
          schoolId,
        },
        schoolType,
      ).then((response) => {
        switch (schoolType) {
          case EducationType.Language:
            return parseToSchoolStudentCardsLanguageCourseFrontEndModel(
              response,
            );
          case EducationType.Secondary:
          case EducationType.Higher:
            return parseToSchoolStudentCardsProgramFrontEndModel(
              response,
              schoolType,
            );
        }
      }),
    );

  return (
    <div className={classes.root}>
      <Helmet>
        <title>Applications{endOfTitle}</title>
      </Helmet>
      {isLoading ? (
        <div className={classes.loading}>
          <Loading />
        </div>
      ) : applications.length ? (
        <div>
          {applications.map((application) => (
            <SchoolStudentApplicationCard
              key={application.id}
              application={application}
              schoolType={schoolType}
            />
          ))}
        </div>
      ) : (
        <Error
          className={classes.error}
          fullHeight={false}
          text={"You have no submitted applications."}
        />
      )}
      <ScrollTop threshold={200} />
    </div>
  );
};
